import {
  HeadsetVersion,
  ControllerBatteryState,
  ControllerConnectionState,
} from "types/productTypes";
import { mapControllerConnectionState } from "@utils/controllerBatteryUtils";
import { ProductActionType } from "../action-types/productActionTypes";
import {
  BATCH_UPDATE,
  CLEAR_TEST_PROGRESS,
  UPDATE_ACTIVE_TEST_ID,
  UPDATE_BATTERY,
  UPDATE_CONTROLLERS,
  UPDATE_CONTROLLER_BATTERY,
  UPDATE_CURRENT_VERSION,
  UPDATE_DEVICE_STATUS,
  UPDATE_IS_EYE_TRACKING_ON,
  UPDATE_ON_TEST_PROGRESS_PAGE,
  UPDATE_PATIENT_ACTION,
  UPDATE_PID,
  UPDATE_SCENE,
  UPDATE_VOLUME,
  UPDATE_STATUS,
  RESET_PRODUCT_STATE,
  UPDATE_PRODUCT_SCOPE,
  SET_LATEST_TEST_INTERACTION_DATE,
  CLEAR_AWAITING_MENU_SCENE,
} from "../constants";
import type { AnyAction } from "@reduxjs/toolkit";
import { setTestStatus } from "@store/tests/testSlice";
import { TestStatus } from "types/testTypes";

export type ProductState = {
  pid: string | null;
  alias: string | null;
  activeTestId: string;
  controllers: ControllerConnectionState | null;
  controllerBattery: ControllerBatteryState | null;
  status: number; // this is the last message opcode
  battery: number;
  currentVersion: HeadsetVersion;
  osVersion: string | null;
  isEyeTrackingOn: boolean;
  scene: string;
  onTestProgressPage: boolean;
  deviceStatus: number;
  patientAction: string;
  lastInteractionDate: Date | null;
  scopeList: string[] | null;
  volume: number;
  lastTestInteractionDate: Date | null;
  permissions: Record<string, boolean> | null;
  awaitingMenuSince: number | null;
};

export const initialState: ProductState = {
  pid: null,
  alias: null,
  activeTestId: "",
  controllers: null,
  controllerBattery: null,
  status: 0,
  battery: 0,
  currentVersion: HeadsetVersion.ver_1_0,
  osVersion: null,
  isEyeTrackingOn: false,
  scene: "",
  onTestProgressPage: false,
  deviceStatus: -1,
  patientAction: "",
  lastInteractionDate: null,
  scopeList: [],
  volume: 80,
  lastTestInteractionDate: null,
  permissions: null,
  awaitingMenuSince: null,
};

const TEST_END_STATUSES: TestStatus[] = [TestStatus.Completed, TestStatus.TerminateRequested];

const productReducer = (
  state: ProductState = initialState,
  action: ProductActionType | AnyAction,
): ProductState => {
  if (action.type === setTestStatus.type) {
    const endedStatus = TEST_END_STATUSES.includes(action.payload?.status);
    if (endedStatus && state.scene?.toLowerCase() !== "menu") {
      return { ...state, awaitingMenuSince: Date.now() };
    }
    return state;
  }
  if (action.type === CLEAR_AWAITING_MENU_SCENE) {
    return state.awaitingMenuSince == null ? state : { ...state, awaitingMenuSince: null };
  }

  switch (action.type) {
    case RESET_PRODUCT_STATE:
      return initialState;
    case UPDATE_PRODUCT_SCOPE:
      return { ...state, scopeList: action.payload };
    case UPDATE_PID:
      return { ...state, pid: action.payload };
    case UPDATE_ACTIVE_TEST_ID:
      return { ...state, activeTestId: action.payload };
    case UPDATE_STATUS:
      return { ...state, status: action.payload };
    case UPDATE_BATTERY:
      return { ...state, battery: action.payload };
    case UPDATE_CONTROLLERS:
      return { ...state, controllers: mapControllerConnectionState(action.payload) };
    case UPDATE_CONTROLLER_BATTERY:
      return { ...state, controllerBattery: action.payload };
    case UPDATE_CURRENT_VERSION:
      return { ...state, currentVersion: action.payload };
    case UPDATE_IS_EYE_TRACKING_ON:
      return { ...state, isEyeTrackingOn: action.payload };
    case UPDATE_SCENE:
      return {
        ...state,
        scene: action.payload.scene,
        lastInteractionDate: action.payload.lastInteraction ?? state.lastInteractionDate,
        awaitingMenuSince:
          action.payload.scene?.toLowerCase() === "menu" ? null : state.awaitingMenuSince,
      };
    case UPDATE_ON_TEST_PROGRESS_PAGE:
      return { ...state, onTestProgressPage: action.payload };
    case UPDATE_DEVICE_STATUS: {
      if (action.payload === state.deviceStatus) {
        return state;
      }
      return { ...state, deviceStatus: action.payload };
    }
    case UPDATE_PATIENT_ACTION:
      return { ...state, patientAction: action.payload };
    case UPDATE_VOLUME:
      return { ...state, volume: action.payload };
    case CLEAR_TEST_PROGRESS:
      return {
        ...state,
        controllers: null,
        controllerBattery: null,
      };
    case BATCH_UPDATE: {
      const { controllers, controllerBattery, ...rest } = action.payload ?? {};
      const nextState = { ...state, ...rest };

      if (controllers !== undefined) {
        nextState.controllers = mapControllerConnectionState(controllers);
      }
      if (controllerBattery !== undefined) {
        nextState.controllerBattery = controllerBattery;
      }

      return nextState;
    }
    case SET_LATEST_TEST_INTERACTION_DATE: {
      return {
        ...state,
        lastTestInteractionDate: action.payload,
      };
    }
    default:
      return state;
  }
};

export default productReducer;
